import React, { useState, useEffect, useCallback } from 'react';
import {
  BookOpen, ArrowUpRight, ArrowDownLeft, CheckCircle, XCircle,
  RefreshCw, Loader2, AlertCircle, X, Copy, Check
} from 'lucide-react';
import { ledgerApi } from '../../services/iso20022Api';

const DIR_FILTERS = [
  { id: '',            label: 'All' },
  { id: 'TO_SWITCH',   label: 'CBS → Switch' },
  { id: 'FROM_SWITCH', label: 'Switch → CBS' },
];

// Operation views — each scopes the ledger to one exchange's message types (FSPIOP-style).
const OPERATIONS = {
  transfers:     { title: 'Transfers',       subtitle: 'Credit transfers exchanged with the Switch — pacs.008 → pacs.002.',  types: ['pacs.008', 'pacs.002'] },
  lookups:       { title: 'Account Lookups', subtitle: 'Account / party verification — acmt.023 → acmt.024.',                types: ['acmt.023', 'acmt.024'] },
  registrations: { title: 'Registrations',   subtitle: 'Account registration — acmt.007 → acmt.010 / acmt.011 + acmt.014.',  types: ['acmt.007', 'acmt.010', 'acmt.011', 'acmt.014'] },
};

function DirectionBadge({ direction }) {
  const toSwitch = direction === 'TO_SWITCH';
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md ${
      toSwitch ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
      {toSwitch ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
      {toSwitch ? 'CBS → Switch' : 'Switch → CBS'}
    </span>
  );
}

function DetailDrawer({ entry, onClose }) {
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!entry) return;
    setData(null); setError(null);
    ledgerApi.detail(entry.messageType, entry.id)
      .then(setData)
      .catch(e => setError(e.response?.data?.message || e.message));
  }, [entry]);

  if (!entry) return null;

  const copyXml = () => {
    if (!data?.rawXml) return;
    navigator.clipboard.writeText(data.rawXml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-gray-900/40" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm px-2 py-1 rounded-md bg-gray-100 text-gray-700">{entry.messageType}</span>
            <DirectionBadge direction={entry.direction} />
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!data && !error && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading message…
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
          )}
          {data && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Status', data.status],
                  ['Signature', data.signatureOk ? 'Valid ✓' : 'Invalid ✗'],
                  ['Direction (raw)', data.rawDirection],
                  ['Time', data.createdAt],
                ].map(([k, v]) => (
                  <div key={k} className="bg-gray-50 rounded-lg p-2">
                    <p className="text-xs text-gray-500">{k}</p>
                    <p className="text-sm text-gray-900 font-medium break-all">{v}</p>
                  </div>
                ))}
              </div>
              {(data.endToEndId || data.txId || data.amount || data.debtorAgt || data.debtorAcct) && (
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1">Transaction details</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ['End-to-end ID', data.endToEndId],
                      ['Transaction ID', data.txId],
                      ['Message ID', data.msgId],
                      ['UETR', data.uetr],
                      ['Amount', data.amount ? `${data.amount} ${data.currency || ''}`.trim() : null],
                      ['Debtor agent (DFSP)', data.debtorAgt],
                      ['Creditor agent (DFSP)', data.creditorAgt],
                      ['Debtor account', data.debtorAcct],
                      ['Creditor account', data.creditorAcct],
                      ['Debtor', data.debtorNm],
                      ['Creditor', data.creditorNm],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="bg-gray-50 rounded-lg p-2">
                        <p className="text-xs text-gray-500">{k}</p>
                        <p className="text-sm text-gray-900 font-medium break-all">{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.signatureDetails && (
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-xs text-gray-500">Signature details</p>
                  <p className="text-sm text-gray-700 break-all">{data.signatureDetails}</p>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-gray-900">Signed XML</p>
                  <button onClick={copyXml}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50">
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}{copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-auto max-h-[60vh] whitespace-pre-wrap break-all">
                  {data.rawXml || '(no payload stored)'}
                </pre>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MessageLedger({ operation } = {}) {
  const op = operation ? OPERATIONS[operation] : null;
  const [entries, setEntries] = useState([]);
  const [types, setTypes]     = useState([]);
  const [filters, setFilters] = useState({ type: '', direction: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => { if (!op) ledgerApi.types().then(setTypes).catch(() => {}); }, [op]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let rows = await ledgerApi.list({ ...filters, type: op ? '' : filters.type, limit: 200 });
      if (op) rows = rows.filter(r => op.types.includes(r.messageType));
      setEntries(rows);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, op]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-primary-600" /> {op ? op.title : 'Message Log'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {op ? op.subtitle : 'Every ISO 20022 message exchanged between your CBS / Payment Gateway and the Switch.'}
          </p>
        </div>
        <button onClick={load} className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {!op && (
          <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white">
            <option value="">All types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}

        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          {DIR_FILTERS.map(d => (
            <button key={d.id} onClick={() => setFilters(f => ({ ...f, direction: d.id }))}
              className={`px-3 py-2 transition-colors ${
                filters.direction === d.id ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {d.label}
            </button>
          ))}
        </div>

        <input value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          placeholder="Status (e.g. SIGNED, RECEIVED)"
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 w-56" />
      </div>

      {/* Table */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Time</th>
              <th className="text-left px-4 py-2.5 font-medium">Type</th>
              <th className="text-left px-4 py-2.5 font-medium">Direction</th>
              <th className="text-left px-4 py-2.5 font-medium">Reference</th>
              <th className="text-left px-4 py-2.5 font-medium">Amount</th>
              <th className="text-left px-4 py-2.5 font-medium">Status</th>
              <th className="text-left px-4 py-2.5 font-medium">Signature</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No messages match these filters.</td></tr>
            )}
            {!loading && entries.map(e => (
              <tr key={`${e.messageType}-${e.id}`}
                  onClick={() => setSelected(e)}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                <td className="px-4 py-2.5 text-gray-500 font-mono text-xs whitespace-nowrap">{e.createdAt}</td>
                <td className="px-4 py-2.5"><span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{e.messageType}</span></td>
                <td className="px-4 py-2.5"><DirectionBadge direction={e.direction} /></td>
                <td className="px-4 py-2.5 font-mono text-xs text-gray-600 whitespace-nowrap">{e.endToEndId || e.txId || '—'}</td>
                <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{e.amount ? `${e.amount} ${e.currency || ''}`.trim() : '—'}</td>
                <td className="px-4 py-2.5 text-gray-700">{e.status}</td>
                <td className="px-4 py-2.5">
                  {e.signatureOk
                    ? <CheckCircle className="w-4 h-4 text-green-500" />
                    : <XCircle className="w-4 h-4 text-red-400" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && entries.length > 0 && (
        <p className="text-xs text-gray-400">{entries.length} message(s) · click a row for the signed XML</p>
      )}

      <DetailDrawer entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
